/**
 * An icon, on web and desktop.
 *
 * The geometry comes from `@aegis/ui` and is shared with mobile; this component
 * is only the browser's way of drawing it. The mobile equivalent is
 * `apps/mobile/src/components/Icon.tsx` — same names, same paths, different
 * renderer.
 *
 * **It takes a name, not a path.** Before this existed, five navigation icons
 * lived inline in `App.tsx`, nine drawing tools in `DrawingToolbar.tsx`, and
 * four more scattered through the replay controls — sixteen path strings that
 * nothing checked and that mobile could not reach. A name is typed against the
 * set, so a typo is a build error rather than an empty square.
 *
 * **Colour is inherited, never passed.** `stroke="currentColor"` means an icon
 * takes the colour of the text around it, which is what makes the whole set
 * follow a theme change with no listener and no re-render.
 */
import { ICON_STROKE, ICON_VIEWBOX, iconPath } from '@aegis/ui';
import type { IconName } from '@aegis/ui';

export interface IconProps {
  name: IconName;
  /**
   * A token size. Defaults to `md`, the rail size.
   *
   * Sizes are metric tokens and therefore per-platform — the phone's are
   * larger. See `packages/ui/src/metrics.web.css`.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * An accessible name. Omit it for an icon that only decorates a label that is
   * already there — the icon is then hidden from assistive technology rather
   * than read out twice.
   */
  label?: string;
  className?: string;
}

export function Icon({ name, size = 'md', label, className }: IconProps): React.JSX.Element {
  const px = `var(--icon-${size})`;
  return (
    <svg
      className={className}
      viewBox={ICON_VIEWBOX}
      /**
       * Size through CSS, not the `width`/`height` attributes.
       *
       * SVG presentation *attributes* take an SVG length and reject `var()` —
       * "Expected length, var(--icon-md)" — which leaves the icon at its default
       * 300×150 and blows the layout apart. The CSS properties do resolve custom
       * properties, so the token stays authoritative. Caught by the browser
       * walk; it typechecks and bundles cleanly either way.
       */
      style={{ width: px, height: px }}
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <path d={iconPath(name)} />
    </svg>
  );
}
